import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Wallet,
  BookOpen,
  CreditCard,
  FileText,
  Shield,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
  Receipt,
  Building2,
  Truck,
  Banknote,
  TrendingUp,
  History,
  X,
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  children?: MenuItem[];
}

interface SidebarProps {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onCloseMobile }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['ledger']));
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    onCloseMobile?.();
    await logout();
    navigate('/login');
  };

  const toggleMenu = (menuTitle: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuTitle)) {
      newExpanded.delete(menuTitle);
    } else {
      newExpanded.add(menuTitle);
    }
    setExpandedMenus(newExpanded);
  };

  const isMenuExpanded = (menuTitle: string) => expandedMenus.has(menuTitle);

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const menuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: '/accountant/dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
    },
    {
      title: 'Vendor Settlements',
      path: '/accountant/settlements',
      icon: <Wallet className="w-5 h-5" />,
    },
    {
      title: 'Manual refund cases',
      path: '/accountant/manual-refund-cases',
      icon: <History className="w-5 h-5" />,
    },
    {
      title: 'Ledger',
      path: '/accountant/ledger',
      icon: <BookOpen className="w-5 h-5" />,
      children: [
        {
          title: 'Customer Ledger',
          path: '/accountant/ledger/customer',
          icon: <Receipt className="w-4 h-4" />,
        },
        {
          title: 'Vendor Ledger',
          path: '/accountant/ledger/vendor',
          icon: <Building2 className="w-4 h-4" />,
        },
        {
          title: 'Delivery Partner Ledger',
          path: '/accountant/ledger/delivery-partner',
          icon: <Truck className="w-4 h-4" />,
        },
        {
          title: 'Razorpay Ledger',
          path: '/accountant/ledger/razorpay',
          icon: <CreditCard className="w-4 h-4" />,
        },
        {
          title: 'Bank Account Ledger',
          path: '/accountant/ledger/bank',
          icon: <Banknote className="w-4 h-4" />,
        },
        {
          title: 'Commission Ledger',
          path: '/accountant/ledger/commission',
          icon: <TrendingUp className="w-4 h-4" />,
        },
        {
          title: 'Profit and Loss Ledger',
          path: '/accountant/ledger/profit-loss',
          icon: <TrendingUp className="w-4 h-4" />,
        },
        {
          title: 'GST Ledger',
          path: '/accountant/ledger/gst',
          icon: <FileText className="w-4 h-4" />,
        },
      ],
    },
    {
      title: 'Reports',
      path: '/accountant/reports',
      icon: <FileText className="w-5 h-5" />,
    },
    {
      title: 'Audit Logs',
      path: '/accountant/audit',
      icon: <Shield className="w-5 h-5" />,
    },
    {
      title: 'Settings',
      path: '/accountant/settings',
      icon: <Settings className="w-5 h-5" />,
    },
    {
      title: 'Help',
      path: '/accountant/help',
      icon: <HelpCircle className="w-5 h-5" />,
    },
  ];

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex h-screen w-[min(16rem,88vw)] flex-shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white transition-transform duration-300 ease-out dark:border-gray-700 dark:bg-gray-800 lg:static lg:z-auto lg:border-r-0 lg:shadow-none lg:transition-[width] lg:duration-300 ${
        mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full lg:translate-x-0'
      } ${collapsed ? 'lg:w-20' : 'lg:w-64'}`}
      aria-label="Accountant navigation"
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200 p-4 dark:border-gray-700">
        <div
          className={`min-w-0 font-semibold text-lg text-primary-600 dark:text-primary-400 ${
            collapsed ? 'lg:hidden' : ''
          }`}
        >
          Accountant
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => onCloseMobile?.()}
            className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden rounded-md bg-gray-100 p-1.5 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 lg:inline-flex"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto py-3 scrollbar-light">
        <ul>
          {menuItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const menuKey = item.title.toLowerCase();
            const isExpanded = hasChildren && isMenuExpanded(menuKey);
            const isActive =
              isPathActive(item.path) ||
              (hasChildren && item.children?.some((child) => isPathActive(child.path)));

            return (
              <li key={item.path}>
                {hasChildren ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (collapsed) {
                          onCloseMobile?.();
                          navigate(item.path);
                          return;
                        }
                        toggleMenu(menuKey);
                      }}
                      className={`flex w-full items-center px-4 py-3 ${
                        collapsed ? 'max-lg:justify-start lg:justify-center' : 'justify-between'
                      } ${
                        isActive
                          ? 'border-r-4 border-primary-600 bg-primary-50 text-primary-600 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div
                        className={`flex items-center ${collapsed ? 'max-lg:gap-3 lg:justify-center' : 'gap-3'}`}
                      >
                        <span className="shrink-0">{item.icon}</span>
                        {!collapsed ? (
                          <span className="min-w-0 truncate">{item.title}</span>
                        ) : (
                          <span className="inline lg:hidden">{item.title}</span>
                        )}
                      </div>
                      {!collapsed &&
                        (isExpanded ? (
                          <ChevronUp className="h-4 w-4 shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 shrink-0" />
                        ))}
                    </button>
                    {!collapsed && isExpanded && item.children && (
                      <ul className="bg-gray-50 dark:bg-gray-900/50">
                        {item.children.map((child) => (
                          <li key={child.path}>
                            <Link
                              to={child.path}
                              onClick={() => onCloseMobile?.()}
                              className={`flex items-center gap-3 px-4 py-2 pl-12 ${
                                isPathActive(child.path)
                                  ? 'border-r-4 border-primary-600 bg-primary-50 text-primary-600 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-400'
                                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <span className="shrink-0">{child.icon}</span>
                              <span className="min-w-0 truncate text-sm">{child.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => onCloseMobile?.()}
                    className={`flex items-center px-4 py-3 ${
                      collapsed ? 'max-lg:justify-start lg:justify-center' : 'gap-3'
                    } ${
                      isPathActive(item.path)
                        ? 'border-r-4 border-primary-600 bg-primary-50 text-primary-600 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <span className="shrink-0">{item.icon}</span>
                    {!collapsed ? (
                      <span className="min-w-0 truncate">{item.title}</span>
                    ) : (
                      <span className="inline lg:hidden">{item.title}</span>
                    )}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="flex flex-shrink-0 items-center border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => void handleLogout()}
          className={`flex w-full items-center gap-2 text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 ${
            collapsed ? 'max-lg:justify-start lg:justify-center' : ''
          }`}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed ? <span>Logout</span> : <span className="inline lg:hidden">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
